import { ReactNode, useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { LayoutActions } from './layout';
import { useAppSelector } from '@/shared/hooks/store';
import ResetIcon from '@/assets/icons/reset-primary.svg';
import VerificationInputs from '../verification-inputs';
import { Alert, Modal } from '@/shared/ui';
import TroublePopup from '../trouble-popup';
import { useAppDispatch } from '@/shared/hooks/store';
import { selectLoginLoading, resetLoading as resetLoadingSelector } from '@/store/slices/auth/selectors';
import { CustomersController, ICustomerVerifyCodeResponse } from '@/shared/endpoints/customers';
import { toast } from 'react-toastify';
import { IErrorResponse } from '@/shared/types/interfaces';

import PenLightGreenIcon from '@/assets/icons/pen-light-green.svg';

import OtherTrouble from '../other-trouble';
import useDeviceType from '@/shared/hooks/useDeviceType';
import CallCenterWorkingHours from '../call-center-working-hours';
import { unfulfilledGenerateVerificationCodeAlertHandler } from '@/shared/helpers/generateCode';

interface IProps {
    email: string;
    initialSourceForDelivery: string;
    defaultVariant?: 'email' | 'phone';
    onSubmit?: (code: ICustomerVerifyCodeResponse) => void;
    initialSignUpOtpTarget?: string;
    onlyCode?: boolean;
    onGetCode?: (res: { payload: ICustomerVerifyCodeResponse; code: string }) => void;
    design?: 'small' | 'large';
    troubleWithModal?: boolean;
    customSendCode?: () => Promise<any>;
    customVerifyCode?: (code: string) => Promise<any>;
    customHeader?: ReactNode;
    disableChangeSource?: boolean;
    sourceEditable?: boolean;
    onEdit?(): void;
    onClose?(): void;
    sendCodeType?: 're-send-identity' | 're-send-authenticated' | 'default';
}

export type Variant = 'email' | 'phone';

/*
    if we are trying to use this ui from the forgot password modal, we need to make sure we don't automatically log the user in 
    take a look at src/modules/auth/pages/login/reset-password/index.tsx
    TODO: we need to expose this flow with the option to handle the code confirmation differently. 
    src/modules/auth/pages/confirm-code/index.tsx is the page that uses this component for the registration flow and it logs the user in automatically
    we need to make sure that the forgot password flow doesn't do that
    we need to expose a prop that allows us to handle the code confirmation differently
    - one major difference is the reset-password is already in a modal so we can't spawn the having trouble modal
    - the ui should simply replace the verification inputs and then fall back to it when the user enters send email or i have the code already. 
    - some additional logic will be needed to handle the code confirmation differently
*/

function maskEmail(email, opts = {}) {
    const { visibleBeforeAt = 2, maskChar = '*', maskLength = 6 } = opts;

    if (typeof email !== 'string') return email;
    const atIndex = email.indexOf('@');
    if (atIndex <= 0) return email;

    const local = email.slice(0, atIndex);
    const domain = email.slice(atIndex + 1);
    if (!domain) return email;

    const visiblePart = local.slice(0, visibleBeforeAt);
    const maskedPart = maskChar.repeat(maskLength);

    return `${visiblePart}${maskedPart}@${domain}`;
}

const VerifyCodeV2 = ({
    email,
    onSubmit,
    initialSourceForDelivery,
    initialSignUpOtpTarget,
    onlyCode,
    onGetCode,
    design = 'large',
    troubleWithModal = true,
    customHeader = '',
    customSendCode,
    customVerifyCode,
    disableChangeSource,
    sourceEditable,
    onEdit,
    onClose,
    sendCodeType = 'default'
}: IProps) => {
    const dispatch = useAppDispatch();
    const { deviceType } = useDeviceType();
    const [code, setCode] = useState('');
    const loading = useAppSelector(selectLoginLoading);
    const resetLoading = useAppSelector(resetLoadingSelector);
    const [resendCodeButtonDisabled, setResendCodeButtonDisabled] = useState(true);
    const [retryTrigger, setRetryTrigger] = useState(true);
    const [variant, setVariant] = useState<Variant>((initialSignUpOtpTarget as Variant) || 'phone');
    const [newSource, setNewSource] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isResentFirstTime, setIsResentFirstTime] = useState(false);
    const [isTroubleOpen, setIsTroubleOpen] = useState(false);
    const [troubleModalShown, setTroubleModalShown] = useState(false);
    const troubleRef = useRef<NodeJS.Timeout | null>(null);
    const [prevVariantAndTarget, setPrevVariantAndTarget] = useState(() => ({
        [initialSignUpOtpTarget]: initialSourceForDelivery,
        ...(initialSignUpOtpTarget !== 'email' && email
            ? { email: maskEmail(email) }
            : {}),
    }));
    const updateCode = useCallback((code: string) => setCode(code), []);

    const handleConfirmCode = async () => {
        const payload = {
            email,
            code
        };

        if (customVerifyCode) {
            await customVerifyCode(code).then(({ meta, payload }) => {
                if (meta.requestStatus !== 'rejected') {
                    const successPayload = payload as ICustomerVerifyCodeResponse;
                    onSubmit?.(successPayload);
                } else {
                    const errorPayload = payload as IErrorResponse;
                    setErrorMessage(errorPayload.message);
                }
            });

            return;
        }

        // confirm code will basically log you in via the thunk handlers
        if (onlyCode) {
            await dispatch(CustomersController.verifyVerificationCode(payload)).then(({ meta, payload }) => {
                if (meta.requestStatus !== 'rejected') {
                    const successPayload = payload as ICustomerVerifyCodeResponse;
                    onGetCode?.({
                        code,
                        payload: successPayload
                    });
                } else {
                    const errorPayload = payload as IErrorResponse;
                    setErrorMessage(
                        errorPayload.message === 'Invalid verification code'
                            ? 'Please check and re-enter the 6-digit code'
                            : errorPayload.message
                    );
                }
            });
        } else {
            await dispatch(CustomersController.confirmCode(payload)).then(({ meta, payload }) => {
                if (meta.requestStatus !== 'rejected') {
                    const successPayload = payload as ICustomerVerifyCodeResponse;
                    onSubmit?.(successPayload);
                } else {
                    const errorPayload = payload as IErrorResponse;
                    setErrorMessage(errorPayload.message);
                }
            });
        }
    };

    const handleResendCode = useCallback(
        async (variant?: Variant, variantChange = false) => {
            setCode('');
            setErrorMessage('');
            const payload: { email: string; type?: 'SMS' | 'EMAIL' } = { email };
            if (variant === 'email') {
                payload.type = 'EMAIL';
            }
            if (retryTrigger) {
                setRetryTrigger(false);
            }
            const request = customSendCode?.() || dispatch(CustomersController.generateVerificationCode(payload));
            await request.then(({ meta, payload }) => {
                if (meta.requestStatus === 'fulfilled' && payload && 'source' in payload) {

                    setPrevVariantAndTarget({ ...prevVariantAndTarget, [variant]: payload.source });
                    const newSourceFromReq = sendCodeType === 're-send-authenticated' ? email : payload.source;
                    setNewSource(newSourceFromReq);
                    toast.success(`Code resent to ${newSourceFromReq}`);
                    setIsResentFirstTime(true)
                    setRetryTrigger(true);
                    setResendCodeButtonDisabled(true);
                } else if (meta.requestStatus === 'rejected') {
                    unfulfilledGenerateVerificationCodeAlertHandler(payload);
                }
            });
        },
        [email, retryTrigger, customSendCode, dispatch, prevVariantAndTarget, sendCodeType]
    );

    const handleChangeVariant = useCallback(
        async (variant: Variant) => {
            setIsTroubleOpen(false);
            setVariant(variant);
            updateCode('');
            await handleResendCode(variant, true);
        },
        [handleResendCode, updateCode]
    );

    useEffect(() => {
        if (retryTrigger) {
            const ref = setTimeout(() => {
                setResendCodeButtonDisabled(false);
            }, 20000);

            return () => clearTimeout(ref);
        }
    }, [retryTrigger]);

    const handleTroubleAction = (variant?: Variant) => {
        setIsTroubleOpen(false);
        if (variant) {
            handleChangeVariant(variant);
        }
        updateCode('');
    };

    const handleOtherTrouble = (close?: boolean) => {
        if (close) {
            onClose?.();
            // window.open('https://medbox.com/book/', '_blank');
        } else {
            setIsTroubleOpen(false);
            updateCode('');
        }
    };


    const resentText = useMemo(() => {
        if (isResentFirstTime) {
            return 'We have resent your code.'
        }
        return 'We have sent your code.'
    }, [isResentFirstTime])


    useEffect(() => {
        // notice how we only open the modal if the user has resent the code and the initial target is phone
        // if the initial is not phone we are actually completely avoiding the having trouble modal
        if (isResentFirstTime && variant === 'phone' && !troubleModalShown) { // TODO CHECK THIS
            const timeoutRef = setTimeout(() => {
                setIsTroubleOpen(true);
                setTroubleModalShown(true);
            }, 20000);
            troubleRef.current = timeoutRef;
            window.addEventListener('click', () => {
                clearTimeout(timeoutRef);
            });
        }
        return () => {
            if (troubleRef.current) {
                clearTimeout(troubleRef.current);
                troubleRef.current = null;
            }
        };
    }, [isResentFirstTime, troubleModalShown, variant]);

    return (
        <>
            {isTroubleOpen && !troubleWithModal ? (
                disableChangeSource || initialSignUpOtpTarget !== 'phone' ? (
                    <OtherTrouble onAction={handleOtherTrouble} />
                ) : (
                    <TroublePopup
                        onAction={handleTroubleAction}
                        variant={variant}
                        initialSourceForDelivery={initialSourceForDelivery}
                        prevVariantAndTarget={prevVariantAndTarget}
                        email={email}
                    />
                )
            ) : (
                <div data-testid="verify-code-container" className="flex flex-col gap-[1.6875rem] lg:gap-5.5">
                    <div className="flex flex-col items-start">
                        {customHeader && (
                            <h2
                                data-testid="verify-code-header"
                                className="mb-[0.4375rem] text-center font-agrandir text-25 sm:my-4.5 lg:text-36"
                            >
                                {customHeader}
                            </h2>
                        )}
                        <h2
                            data-testid="verify-code-title"
                            className={`font-agrandir ${customHeader ? 'text-26' : 'text-26 sm:text-35'} mb-4.5`}
                        >
                            {variant === 'phone' ? (
                                <>
                                    Please enter the <span className="text-primary">6-digit code</span> we’ve sent to your phone number
                                </>
                            ) : (
                                <>
                                    Please enter the <span className="text-primary">6-digit code</span> we’ve sent to your email
                                </>
                            )}
                        </h2>
                        {sourceEditable ? (
                            <p className="flex justify-center text-center font-dmSans text-xl font-bold sm:text-22">
                                {newSource || initialSourceForDelivery}
                                <button className="ml-3 flex text-lg text-primary-400" onClick={onEdit}>
                                    <img width={14} className="mr-1 w-[0.875rem]" src={PenLightGreenIcon} alt="pencil" />
                                    Edit
                                </button>
                            </p>
                        ) : (
                            <p className={`text-center font-dmSans text-xl font-bold sm:text-27`}>
                                {newSource || initialSourceForDelivery}
                            </p>
                        )}
                    </div>
                    <div data-testid="verify-code-form-container" className="flex w-full flex-col items-start gap-7">
                        <VerificationInputs errorMessage={errorMessage} updateCode={updateCode} code={code} />
                    </div>
                    <div className="flex w-full flex-col items-start gap-7">
                        <LayoutActions
                            btnTestId="button-verify-code-confirm-code"
                            disabled={loading || code.length !== 6 || resetLoading}
                            onClick={handleConfirmCode}
                        />
                        <button
                            className={`flex items-center justify-center font-dmSans text-base font-bold text-primary underline sm:text-lg ${resendCodeButtonDisabled ? 'cursor-not-allowed opacity-50' : ''
                                }`}
                            onClick={() => handleResendCode(variant)}
                            disabled={resendCodeButtonDisabled || resetLoading}
                            data-testid="button-verify-code-resend-code"
                        >
                            <img src={ResetIcon} alt="" />
                            Resend Code
                        </button>
                    </div>
                    {
                        (disableChangeSource ? (
                            <Alert
                                label={resentText}
                                variant="success"
                                className="gap-1 px-6 py-4 shadow-alert"
                                withIcon={false}
                                childrenClassName="px-0"
                                removeBorder
                            >
                                <ul className="mb-[0.625rem] flex list-disc flex-col px-5">
                                    {variant === 'email' && (
                                        <li className="font-dmSans text-17" data-testid="verify-code-spam-text">
                                            Don’t forget to check your spam folder
                                        </li>
                                    )}
                                    <li data-testid="verify-code-call-option-text" className="font-dmSans text-15 sm:text-17">
                                        If you don’t receive your code give us a call{' '}
                                        {deviceType === 'mobile' ? (
                                            <a
                                                data-testid="verify-code-call-option-link"
                                                href="tel:+18002707091"
                                                className="font-dmSansMedium text-15 font-bold text-primary underline sm:text-17"
                                            >
                                                (866) 865-6771
                                            </a>
                                        ) : (
                                            <span
                                                data-testid="verify-code-call-option-span"
                                                className="font-dmSansMedium text-15 font-bold text-primary underline sm:text-17"
                                            >
                                                (866) 865-6771
                                            </span>
                                        )}{' '}
                                        during our operating hours:
                                    </li>
                                </ul>

                                <CallCenterWorkingHours />
                            </Alert>
                        ) : (
                            <Alert
                                label={resentText}
                                variant="success"
                                className="gap-1 px-6 py-4 shadow-alert"
                                withIcon={false}
                                removeBorder
                            >
                                <ul className="flex list-disc flex-col">
                                    {variant === 'phone' ? (
                                        <>
                                            <li className="font-dmSans text-15 sm:text-17">
                                                Have us resend your code{' '}
                                                <button className="text-primary underline" onClick={() => handleChangeVariant('email')}>
                                                    By Email
                                                </button>{' '}
                                                instead
                                            </li>
                                        </>
                                    ) : (
                                        <>
                                            <li className="font-dmSans text-17">Don’t forget to check your spam folder</li>
                                            {initialSignUpOtpTarget !== 'email' && (
                                                <li className="font-dmSans text-17">
                                                    Have us resend your code{' '}
                                                    <button className="text-primary underline" onClick={() => handleChangeVariant('phone')}>
                                                        By Phone
                                                    </button>{' '}
                                                    instead
                                                </li>
                                            )}
                                        </>
                                    )}
                                </ul>
                            </Alert>
                        ))}
                </div>
            )}

            <Modal
                dataTestId={disableChangeSource ? 'other-trouble' : 'email-trouble'}
                open={isTroubleOpen && troubleWithModal}
                closeOnClickOutside={false}
                onClose={() => setIsTroubleOpen(false)}
            >
                {disableChangeSource ? (
                    <OtherTrouble onAction={handleOtherTrouble} />
                ) : (
                    <TroublePopup
                        onAction={handleTroubleAction}
                        variant={variant}
                        initialSourceForDelivery={initialSourceForDelivery}
                        prevVariantAndTarget={prevVariantAndTarget}
                        email={email}
                    />
                )}
            </Modal>
        </>
    );
};

export default VerifyCodeV2;
